import React, { useState, useEffect, useRef } from "react";
import { useAppContext } from "../context/appContext";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";

const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const WARNING_TIME = 30 * 1000; // warning time, 30 seconds before

const IdleTimerContainer = () => {
  const [showModal, setShowModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(WARNING_TIME / 1000);
  const { user, logoutUser } = useAppContext();
  const navigate = useNavigate();

  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const resetTimers = () => {
    lastActivityRef.current = Date.now();

    // clear existing timers.
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

    // hide warning modal if it's open
    setShowModal(false);

    // set up new timers
    warningTimerRef.current = setTimeout(() => {
      setShowModal(true);
      setTimeLeft(WARNING_TIME / 1000);

      // start countdown
      countdownTimerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(countdownTimerRef.current);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }, IDLE_TIMEOUT - WARNING_TIME);

    idleTimerRef.current = setTimeout(() => {
      logoutUser();
      navigate("/");
    }, IDLE_TIMEOUT);
  };

  const handleUserActivity = () => {
    // only proceed if user is logged in
    if (!user) return;

    // only reset timers if user is not already on the warning modal
    if (!showModal) {
      resetTimers();
    }
  };

  const handleStayLoggedIn = () => {
    setShowModal(false);
    resetTimers();
  };

  const handleLogout = async () => {
    setShowModal(false);
    await logoutUser();
    navigate("/");
  };

  useEffect(() => {
    if (!user) return;

    // set up event listeners for user activity
    const events = ["mousedown", "keydown", "touchstart", "scroll", "click"];

    // add event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    resetTimers();

    // clean up on unmount
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });

      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Added user to dependencies

  // only render the modal if user is logged in
  if (!user) return null;

  return (
    <Modal show={showModal} onHide={handleStayLoggedIn} backdrop="static" keyboard={false} centered>
      <Modal.Header>
        <Modal.Title>Session Timeout Warning</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>You've been inactive for a while. For security reasons, you will be automatically logged out in {timeLeft} seconds.</p>
        <p>Do you want to stay logged in?</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleLogout}>
          Log Out Now
        </Button>
        <Button variant="primary" onClick={handleStayLoggedIn}>
          Stay Logged In
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IdleTimerContainer;
